import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Collapse, Dialog, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useHistory, useLocation, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CloseIcon from "@mui/icons-material/Close";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import i18next from "i18next";
import { BRAND, FONT, RADIUS, SHADOW, useLightStyles, CONTACT_STATUS, MEDIA } from "./lightTheme";
import { Context } from "states/Store";
import { formatDt, normColor } from "./utils/lightUtils";
import StopConfirmDialog from "./components/StopConfirmDialog";
import useMentionAutocomplete from "./hooks/useMentionAutocomplete";
import LightNotesList from "./components/LightNotesList";
import DossierAccordion from "./components/DossierAccordion";
import { getThirdparty, getTPContacts, createTPContacts, deleteTPContacts, reinviteTPContacts, getThirdPartyIndicators, stopThirdparty, reactivateThirdparty } from "services/edge/ThirdpartyService";
import { getThirdpartyAttributes, getAccountAttributesByBondType } from "services/edge/AttributesService";
import { getAccountDossiersSearch } from "services/edge/DossierService";
import { getContextThirdpartyByTPid, getContextInstances } from "services/edge/ContexteService";
import { getAllNoteTP, createNoteTP, deleteNoteTP } from "services/edge/NoteService";
import { getAccountUsers } from "services/edge/UserService";
import { formatCalculationResult } from "services/common/CalculatorUtils";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, SUCCESS, getUserLanguages, REGEXP_EMAIL } from "services/common/Constants";
import { RoleBasedAccessControlContext, PrivilegeEnum } from "services/common/RolesUtils";
import ROUTES, { getUrlWithAccountId } from "config/Routes";
import LightLoader from "./LightLoader";
import LightEditableAttributes from "./LightEditableAttributes";

// ─── Map iframe (OpenStreetMap + Nominatim geocoding) ─────────────────────────
function MapIframe({ street, postCode, city, countryCode }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    setSrc(null);
    setError(false);

    const normalizedCity = (city ?? "").replace(/\s+\d+e?$/i, "").trim();
    const queries = [
      [street, postCode, normalizedCity, countryCode].filter(Boolean).join(", "),
      [postCode, normalizedCity, countryCode].filter(Boolean).join(", "),
      [normalizedCity, countryCode].filter(Boolean).join(", "),
    ].filter((q, i, arr) => q.length > 0 && arr.indexOf(q) === i);

    (async () => {
      for (const q of queries) {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`,
            { signal: controller.signal }
          );
          if (!res.ok) continue;
          const data = await res.json();
          if (data?.[0]) {
            const { lat, lon } = data[0];
            const d = 0.005;
            setSrc(`https://www.openstreetmap.org/export/embed.html?bbox=${lon - d},${lat - d},${+lon + d},${+lat + d}&layer=mapnik&marker=${lat},${lon}`);
            return;
          }
        } catch (err) {
          if (err.name === "AbortError") return;
        }
      }
      setError(true);
    })();

    return () => controller.abort();
  }, [street, postCode, city, countryCode]);

  if (error) {
    return (
      <Box style={{ height: "420px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.5rem", backgroundColor: BRAND.background, color: BRAND.textMuted, fontFamily: FONT.family, ...FONT.body }}>
        <LocationOnOutlinedIcon style={{ fontSize: "32px", opacity: 0.4 }} />
        <span>{i18next.t("light.detail.mapUnavailable")}</span>
      </Box>
    );
  }

  if (!src) {
    return (
      <Box style={{ height: "420px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: BRAND.background }}>
        <CircularProgress size={22} style={{ color: BRAND.primary }} />
      </Box>
    );
  }

  return (
    <iframe
      title="map"
      width="100%"
      height="420"
      frameBorder="0"
      style={{ border: 0, display: "block" }}
      src={src}
      loading="lazy"
    />
  );
}

// ─── Local styles (specific to this page) ─────────────────────────────────────
export const useThirdpartyDetailStyles = makeStyles()(() => ({
  heroPanelLabel: {
    ...FONT.label, letterSpacing: "0.09em", color: BRAND.textMuted,
    fontFamily: FONT.family, marginBottom: "0.25rem",
  },
  heroPanelRow: {
    display: "flex", alignItems: "flex-start", gap: "0.5rem",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family,
  },
  heroPanelValue: {
    ...FONT.body, fontWeight: 500, color: BRAND.textMain, fontFamily: FONT.family,
  },

  // ── Dossier cards ──
  dossierList: {
    display: "flex", flexDirection: "column", gap: "0.5rem",
    padding: "1.25rem 1.5rem",
    [MEDIA.smDown]: { padding: "1rem" },
  },
  dossierCard: {
    display: "flex", alignItems: "center", gap: "1rem",
    padding: "0.75rem 1rem", borderRadius: RADIUS.md,
    border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white,
    cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: BRAND.primary, boxShadow: SHADOW.soft },
    [MEDIA.smDown]: { flexWrap: "wrap", gap: "0.5rem", padding: "0.75rem" },
  },
  dossierIcon: {
    width: "32px", height: "32px", borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  dossierInfo: { flex: 1, minWidth: 0 },
  dossierName: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, lineHeight: 1.3,
  },
  dossierMeta: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family },
  dossierStatus: {
    display: "inline-flex", alignItems: "center", borderRadius: "20px",
    padding: "3px 10px", ...FONT.meta, fontFamily: FONT.family,
    whiteSpace: "nowrap", flexShrink: 0,
  },
  dossierAction: {
    display: "flex", alignItems: "center", gap: "0.25rem",
    ...FONT.meta, color: BRAND.primary, fontFamily: FONT.family,
    whiteSpace: "nowrap", flexShrink: 0,
    opacity: 0.7, transition: "opacity 0.15s",
    "&:hover": { opacity: 1 },
  },
  dossierChevron: {
    fontSize: "18px", color: BRAND.textMuted,
    transition: "transform 0.25s",
    flexShrink: 0,
  },

  // ── Accordion content ──
  accordionBody: {
    borderTop: `1px solid ${BRAND.border}`,
    padding: "1rem 1rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "0.75rem",
    backgroundColor: BRAND.background,
  },
  accordionSection: {
    display: "flex", flexDirection: "column", gap: "0.35rem",
  },
  accordionSectionLabel: {
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    display: "flex", alignItems: "center", gap: "0.4rem",
  },

  // ── Doc rows (accordion) ──
  docRow: {
    display: "flex", alignItems: "center", gap: "0.75rem",
    padding: "0.6rem 0.75rem", borderRadius: RADIUS.sm,
    backgroundColor: BRAND.white, border: `1px solid ${BRAND.border}`,
    cursor: "pointer", transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": { borderColor: BRAND.primary, boxShadow: SHADOW.soft },
    [MEDIA.smDown]: { flexWrap: "wrap", gap: "0.5rem" },
  },
  docName: {
    ...FONT.body, fontWeight: 600, color: BRAND.textMain,
    fontFamily: FONT.family, flex: 1, minWidth: 0,
    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
    [MEDIA.smDown]: {
      flex: "1 1 100%", whiteSpace: "normal",
      overflow: "visible", textOverflow: "clip",
      wordBreak: "break-word",
    },
  },
  docMeta: {
    ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
    flexShrink: 0, whiteSpace: "nowrap",
  },
  docStatusChip: {
    display: "inline-flex", alignItems: "center",
    borderRadius: "20px", padding: "3px 10px",
    ...FONT.tiny, fontFamily: FONT.family, flexShrink: 0,
  },
  docActionBtn: {
    display: "flex", alignItems: "center", gap: "0.25rem",
    ...FONT.meta, color: BRAND.primary, fontFamily: FONT.family,
    whiteSpace: "nowrap", flexShrink: 0, cursor: "pointer",
    background: "none", border: "none", padding: 0,
    opacity: 0.7, transition: "opacity 0.15s",
    "&:hover": { opacity: 1 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },

  // ── Notes (accordion) ──
  noteInput: {
    display: "flex", gap: "0.5rem", alignItems: "center",
  },
  noteTextarea: {
    flex: 1, borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`,
    padding: "0.5rem 0.65rem", fontFamily: FONT.family,
    ...FONT.body, resize: "none", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
    backgroundColor: BRAND.white, color: BRAND.textMain,
    lineHeight: 1.5, minHeight: "44px",
  },
  noteSendBtn: {
    height: "44px", width: "44px", borderRadius: RADIUS.sm,
    border: "none", backgroundColor: BRAND.primary,
    color: BRAND.white, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.35, cursor: "not-allowed" },
  },
  noteItem: {
    padding: "0.5rem 0.75rem", borderRadius: RADIUS.sm,
    backgroundColor: BRAND.white, border: `1px solid ${BRAND.border}`,
    display: "flex", flexDirection: "column", gap: "0.25rem",
  },
  noteHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  noteAuthor: { ...FONT.body, fontWeight: 700, color: BRAND.textMain, fontFamily: FONT.family },
  noteDate: { ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family },
  noteContent: {
    ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family,
    lineHeight: 1.5, whiteSpace: "pre-wrap",
  },
  noteDeleteBtn: {
    padding: "2px 4px", borderRadius: RADIUS.sm, border: "none",
    background: "transparent", cursor: "pointer",
    color: BRAND.textMuted, display: "flex", alignItems: "center",
    transition: "color 0.15s",
    "&:hover": { color: "#E84343" },
  },
  mentionInputWrap: { position: "relative" },
  mentionDropdown: {
    position: "absolute", zIndex: 200,
    top: "100%", left: 0, right: 0, marginTop: "4px",
    backgroundColor: BRAND.white, border: `1.5px solid ${BRAND.border}`,
    borderRadius: RADIUS.sm, boxShadow: SHADOW.strong,
    overflow: "hidden", maxHeight: "150px", overflowY: "auto",
  },
  mentionItem: {
    display: "flex", alignItems: "center", gap: "0.5rem",
    padding: "0.4rem 0.65rem", cursor: "pointer",
    transition: "background 0.1s",
    "&:hover": { backgroundColor: BRAND.background },
  },
  mentionItemActive: { backgroundColor: BRAND.background },
  mentionAvatar: {
    width: "22px", height: "22px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    display: "flex", alignItems: "center", justifyContent: "center",
    ...FONT.tiny, fontWeight: 700, flexShrink: 0,
  },
  mentionName: { ...FONT.body, fontWeight: 500, color: BRAND.textMain, fontFamily: FONT.family },

  // ── Contacts ──
  contactTableWrap: {
    width: "100%",
    [MEDIA.smDown]: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
  },
  contactTable: {
    width: "100%", borderCollapse: "collapse",
    [MEDIA.smDown]: { minWidth: "560px" },
  },
  contactTh: {
    padding: "0.6rem 1rem",
    ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family,
    textAlign: "left", borderBottom: `1px solid ${BRAND.border}`,
    backgroundColor: BRAND.background, whiteSpace: "nowrap",
  },
  contactTr: {
    borderBottom: `1px solid ${BRAND.border}`,
    transition: "background 0.1s",
    "&:last-child": { borderBottom: "none" },
    "&:hover": { backgroundColor: BRAND.background },
  },
  contactTd: {
    padding: "0.75rem 1rem",
    ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family,
    verticalAlign: "middle",
  },
  contactAvatar: {
    width: "30px", height: "30px", borderRadius: "50%",
    backgroundColor: BRAND.primary, color: BRAND.white,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    ...FONT.meta, fontWeight: 700, fontFamily: FONT.family,
    flexShrink: 0, marginRight: "0.6rem",
  },
  contactName: {
    fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family,
    ...FONT.body,
  },
  contactEmail: {
    ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family,
  },
  contactActions: {
    display: "flex", gap: "0.35rem", alignItems: "center",
  },
  contactActionBtn: {
    width: "28px", height: "28px", borderRadius: "7px",
    border: `1px solid ${BRAND.border}`, backgroundColor: BRAND.white,
    color: BRAND.textMuted, cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    transition: "border-color 0.15s, color 0.15s, background 0.15s",
    "&:hover": { borderColor: BRAND.primary, color: BRAND.primary, backgroundColor: `${BRAND.primary}0D` },
    "&:disabled": { opacity: 0.3, cursor: "not-allowed" },
  },
  contactActionDelete: {
    "&:hover": { borderColor: "#EF4444", color: "#EF4444", backgroundColor: "#FEF2F2" },
  },
  emptyContacts: {
    padding: "2.5rem 1.5rem", textAlign: "center",
    ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family,
  },

  // ── Stop confirmation dialog ──
  stopDialogBody: {
    padding: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem",
  },
  stopDialogText: {
    ...FONT.body, color: BRAND.textMain, fontFamily: FONT.family, lineHeight: 1.6,
  },
  stopDialogTextarea: {
    width: "100%", borderRadius: RADIUS.sm,
    border: `1.5px solid ${BRAND.border}`,
    padding: "0.6rem 0.75rem", fontFamily: FONT.family,
    ...FONT.body, resize: "none", outline: "none",
    transition: "border-color 0.15s",
    "&:focus": { borderColor: BRAND.primary },
    color: BRAND.textMain, lineHeight: 1.5,
  },
  stopDialogActions: {
    display: "flex", gap: "0.75rem", justifyContent: "flex-end", paddingTop: "0.5rem",
  },
  btnDanger: {
    padding: "0.5rem 1.25rem", borderRadius: RADIUS.md, border: "none",
    backgroundColor: "#DC2626", color: BRAND.white, fontFamily: FONT.family,
    ...FONT.h3, cursor: "pointer", transition: "opacity 0.15s",
    "&:hover": { opacity: 0.88 },
    "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
}));

// ─── Component ─────────────────────────────────────────────────────────────────
export default function LightThirdpartyDetail() {
  const { classes: s } = useThirdpartyDetailStyles();
  const { classes: light } = useLightStyles();
  const { thirdpartyId } = useParams();
  const [state, dispatch] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);
  const canCreateContact      = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_CONTACT);
  const canDeleteContact      = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_DELETE_CONTACT);
  const canViewTpNotes        = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_NOTES);
  const canCreateTpNotes      = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_CREATE_THIRD_PARTY_NOTES);
  const canViewTpIndicators   = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_INDICATOR);
  const canViewTpAttributes   = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_THIRD_PARTY_ATTRIBUTE_VALUE);
  const canStopTp             = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_ARCHIVE_DOSSIER);
  const canReactivateTp       = hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_EDIT_DOSSIER);
  const history = useHistory();
  const location = useLocation();
  const listState = location.state ?? {}; // filters passed from list page

  const [tp, setTp] = useState(null);
  const [attrs, setAttrs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteLang, setInviteLang] = useState(i18next.language || "fr");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [editAttrsOpen, setEditAttrsOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [principalsOpen, setPrincipalsOpen] = useState(false);
  // Collapse legal info block by default on mobile, open on desktop
  const [legalInfoOpen, setLegalInfoOpen] = useState(() => typeof window === "undefined" || window.innerWidth >= 600);
  const [hasTpAttrDefs, setHasTpAttrDefs] = useState(false);
  const [hasDossierAttrDefs, setHasDossierAttrDefs] = useState(false);

  // Contexts
  const [tpContexts, setTpContexts] = useState([]);
  const dossierContextMap = useMemo(() => {
    const map = {};
    tpContexts.forEach((ctx) => {
      const ref = ctx.instanceInfo?.reference || "";
      (ctx.dossierIds ?? []).forEach((did) => { map[did] = ref; });
    });
    return map;
  }, [tpContexts]);

  // Stop thirdparty
  const [stopTpDialogOpen, setStopTpDialogOpen] = useState(false);
  const [stopTpReason, setStopTpReason] = useState("");
  const [stoppingTp, setStoppingTp] = useState(false);

  // Archived evaluations
  const [archivedEvalsCount, setArchivedEvalsCount] = useState(0);
  const [archivedEvals, setArchivedEvals] = useState([]);
  const [archivedEvalsOpen, setArchivedEvalsOpen] = useState(false);
  const [archivedEvalsLoaded, setArchivedEvalsLoaded] = useState(false);
  const [archivedEvalsLoading, setArchivedEvalsLoading] = useState(false);

  const handleToggleArchivedEvals = () => {
    const next = !archivedEvalsOpen;
    setArchivedEvalsOpen(next);
    if (next && !archivedEvalsLoaded && tp?.name) {
      setArchivedEvalsLoading(true);
      getAccountDossiersSearch(
        state.account.id, false, null,
        { page: 0, rowPerPage: 50 },
        { dossiers: [{ name: tp.name }] },
        [{ field: "creationDate", sort: "desc" }],
        dispatch,
      ).then((r) => {
        setArchivedEvals(r?.content ?? []);
      }).catch(() => setArchivedEvals([]))
        .finally(() => { setArchivedEvalsLoaded(true); setArchivedEvalsLoading(false); });
    }
  };

  // TP Notes
  const [tpNotes, setTpNotes] = useState([]);
  const [tpNotesOpen, setTpNotesOpen] = useState(false);
  const [tpNoteText, setTpNoteText] = useState("");
  const [tpSavingNote, setTpSavingNote] = useState(false);
  const [tpAccountUsers, setTpAccountUsers] = useState([]);
  const tpMention = useMentionAutocomplete(tpAccountUsers, tpNoteText, setTpNoteText);

  const load = useCallback(async () => {
    if (!state.account?.id || !thirdpartyId) return;
    setLoading(true);
    try {
      const tpRes = await getThirdparty(state.account.id, thirdpartyId, dispatch);
      const tpData = tpRes?.content;
      setTp(tpData);

      // Optional data in parallel
      await Promise.allSettled([
        getThirdpartyAttributes(state.account.id, thirdpartyId, null)
          .then((r) => setAttrs((r?.content ?? []).filter((a) => a.values?.length > 0))),
        getTPContacts(state.account.id, thirdpartyId, dispatch)
          .then((r) => setContacts(r?.content ?? [])),
        tpData?.name
          ? getAccountDossiersSearch(
              state.account.id, true, null,
              { page: 0, rowPerPage: 50 },
              { dossiers: [{ name: tpData.name }] },
              [{ field: "creationDate", sort: "desc" }],
              dispatch,
            ).then((r) => {
              setEvaluations(r?.content ?? []);
            })
          : Promise.resolve(),
        getThirdPartyIndicators(state.account.id, thirdpartyId, dispatch)
          .then((r) => setIndicators(r?.content ?? [])),
        getAllNoteTP(state.account.id, thirdpartyId, dispatch)
          .then((r) => setTpNotes(r?.content ?? [])),
        getAccountUsers(state.account.id, dispatch)
          .then((r) => setTpAccountUsers(r?.content ?? [])),
        tpData?.name
          ? getAccountDossiersSearch(
              state.account.id, false, null,
              { page: 0, rowPerPage: 50 },
              { dossiers: [{ name: tpData.name }] },
              [{ field: "creationDate", sort: "desc" }],
              dispatch,
            ).then((r) => setArchivedEvalsCount((r?.content ?? []).length))
          : Promise.resolve(),
        getAccountAttributesByBondType(state.account.id, "thirdparty", null)
          .then((r) => setHasTpAttrDefs((r?.content ?? []).length > 0))
          .catch(() => {}),
        getAccountAttributesByBondType(state.account.id, "dossier", null)
          .then((r) => setHasDossierAttrDefs((r?.content ?? []).length > 0))
          .catch(() => {}),
        Promise.all([
          getContextThirdpartyByTPid(state.account.id, thirdpartyId, dispatch),
          getContextInstances(state.account.id, true, dispatch),
        ]).then(([ctxTps, instances]) => {
          const byId = Object.fromEntries((instances?.content ?? []).map((i) => [String(i.id), i]));
          const arr = (Array.isArray(ctxTps) ? ctxTps : [])
            .map((ct) => ({ ...ct, instanceInfo: byId[String(ct.ctxInstanceId)] ?? null }))
            .filter((ct) => ct.instanceInfo);
          setTpContexts(arr);
        }).catch(() => {}),
      ]);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setLoading(false);
    }
  }, [state.account?.id, thirdpartyId, dispatch]);

  useEffect(() => { load(); }, [load]);

  // Reopen doc viewer after returning from external form
  const [reopenDoc, setReopenDoc] = useState(null);
  useEffect(() => {
    const rd = location.state?.reopenDoc;
    if (rd && tp) {
      setReopenDoc(rd);
      history.replace(location.pathname, { ...location.state, reopenDoc: undefined });
    }
  }, [tp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Contact handlers ──
  const handleReinviteContact = async (contact) => {
    try {
      await reinviteTPContacts(state.account.id, contact.tpId ?? thirdpartyId, contact.pcuId, dispatch);
      const res = await getTPContacts(state.account.id, thirdpartyId, dispatch);
      setContacts(res?.content ?? []);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    }
  };

  const [deleteContactTarget, setDeleteContactTarget] = useState(null);
  const [deletingContact, setDeletingContact] = useState(false);
  const handleConfirmDeleteContact = async () => {
    if (!deleteContactTarget) return;
    setDeletingContact(true);
    try {
      await deleteTPContacts(state.account.id, [{ userId: deleteContactTarget.userId ?? deleteContactTarget.pcuId, tpAccountId: deleteContactTarget.tpaccountid ?? deleteContactTarget.tpAccountId ?? deleteContactTarget.tpId }], dispatch);
      setContacts((prev) => prev.filter((c) => (c.userId ?? c.pcuId) !== (deleteContactTarget.userId ?? deleteContactTarget.pcuId)));
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.contactDeleted") } });
      setDeleteContactTarget(null);
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setDeletingContact(false);
    }
  };

  const handleInviteContact = async () => {
    const trimmed = inviteEmail.trim();
    if (!trimmed) return;
    if (!REGEXP_EMAIL.test(trimmed)) { setInviteError(i18next.t("light.profile.invalidEmail")); return; }
    setInviteError("");
    setInviteLoading(true);
    try {
      await createTPContacts(state.account.id, thirdpartyId, { email: trimmed, lang: inviteLang }, dispatch);
      setInviteEmail("");
      setInviteOpen(false);
      setInviteError("");
      const res = await getTPContacts(state.account.id, thirdpartyId, dispatch);
      setContacts(res?.content ?? []);
    } catch (e) {
      const errMsg = e?.message?.messages?.error?.[0]?.message;
      const errCode = e?.message?.messages?.error?.[0]?.code;
      if (errMsg?.includes("Blacklisted")) {
        setInviteError(i18next.t("infos.EMAIL_BLACKLISTED_ERROR"));
      } else if (errMsg?.includes("does not wish to have any additional contacts")) {
        setInviteError(i18next.t("infos.COMPTE_BLACKLISTED_ERROR"));
      } else if (errMsg?.includes("Invalid email")) {
        setInviteError(i18next.t("light.profile.invalidEmail"));
      } else if (errCode === "E133") {
        const details = errMsg?.match(/\(([^)]+)\)/)?.[1] ?? "";
        setInviteError(i18next.t("invalidEmail", { details }));
      } else {
        setInviteError(getApiErrorMessageCode(e));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  // ── Stop thirdparty handler ──
  const handleStopThirdparty = async () => {
    setStoppingTp(true);
    try {
      await stopThirdparty(state.account.id, thirdpartyId, stopTpReason.trim(), dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.tpStopped") } });
      setStopTpDialogOpen(false);
      setStopTpReason("");
      history.push({ pathname: getUrlWithAccountId(state.account.id, "light/thirdparties"), state: { archived: true } });
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setStoppingTp(false);
    }
  };

  const [reactivatingTp, setReactivatingTp] = useState(false);
  const handleReactivateThirdparty = async () => {
    setReactivatingTp(true);
    try {
      await reactivateThirdparty(state.account.id, thirdpartyId, dispatch);
      dispatch({ type: "ALERT", alert: { type: SUCCESS, msg: i18next.t("light.detail.tpReactivated") } });
      load();
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setReactivatingTp(false);
    }
  };

  const handleAddTpNote = async () => {
    if (!tpNoteText.trim()) return;
    setTpSavingNote(true);
    try {
      const mentionRegexp = /@([\w\u00C0-\u017E]+ [\w\u00C0-\u017E]+)/g;
      const matched = [...tpNoteText.matchAll(mentionRegexp)].map((m) => m[1]);
      const filteredUsers = tpMention.selectedMentions
        .filter((m) => matched.includes(m.nomPrenom))
        .map((m) => m.userOid);
      await createNoteTP(state.account.id, thirdpartyId, {
        content: tpNoteText.trim(),
        selectedUsersIds: filteredUsers,
      }, dispatch);
      setTpNoteText("");
      tpMention.resetMentions();
    } catch (_) { /* silent */ }
    setTpSavingNote(false);
    try {
      const res = await getAllNoteTP(state.account.id, thirdpartyId, dispatch);
      setTpNotes(res?.content ?? []);
    } catch (_) { /* silent */ }
  };

  const handleDeleteTpNote = async (noteOId) => {
    try {
      await deleteNoteTP(state.account.id, thirdpartyId, noteOId, dispatch);
      setTpNotes((prev) => prev.filter((n) => n.noteOId !== noteOId));
    } catch (_) { /* silent */ }
  };


  // ── Render ──
  if (loading) return <LightLoader overlay label={i18next.t("light.detail.loadingSupplier")} />;
  if (!tp) return null;

  const identifiers = [
    tp.siret && { label: "Siret", value: tp.siret },
    tp.siren && { label: "Siren", value: tp.siren },
    tp.tva   && { label: "TVA",   value: tp.tva },
    tp.duns && !tp.dunsEncrypted && { label: "DUNS", value: tp.duns },
  ].filter(Boolean);

  return (
    <>
    <Box className={light.pageRoot}>
      {/* Header */}
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner}>
          <Box
            component="a"
            href={listState.fromContextId
              ? getUrlWithAccountId(state.account.id, `light/contexts/${listState.fromContextId}`)
              : getUrlWithAccountId(state.account.id, ROUTES.LIGHT_THIRDPARTIES)}
            className={light.backBtnWhite}
            onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault();
              if (listState.fromContextId) {
                history.push(getUrlWithAccountId(state.account.id, `light/contexts/${listState.fromContextId}`));
              } else {
                history.push({ pathname: getUrlWithAccountId(state.account.id, ROUTES.LIGHT_THIRDPARTIES), state: { ...listState, archived: !tp.onboarded ? true : listState.listArchived ?? false } });
              }
            }}
            style={{ textDecoration: "none" }}
          >
            <ArrowBackIcon style={{ fontSize: "16px" }} />
            {listState.fromContextId ? i18next.t("light.common.back") : i18next.t("light.detail.mySuppliers")}
          </Box>
          {!tp.onboarded && (
            <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", backgroundColor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.md, padding: "0.4rem 1rem", marginBottom: "0.25rem" }}>
              <StopCircleOutlinedIcon style={{ fontSize: "15px", color: "#FCA5A5" }} />
              <Typography style={{ ...FONT.body, fontWeight: 700, color: "#FCA5A5", fontFamily: FONT.family }}>{i18next.t("light.detail.archivedSupplier")}</Typography>
              {tp.stoppingDate && (
                <Typography style={{ ...FONT.small, color: "rgba(255,255,255,0.6)", fontFamily: FONT.family, marginLeft: "0.25rem" }}>
                  {i18next.t("light.detail.since", { date: formatDt(tp.stoppingDate) })}
                </Typography>
              )}
            </Box>
          )}
          <Box style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem", minWidth: 0 }}>
            <Typography className={light.pageTitleWhite} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{tp.name}</Typography>
            {tp.onboarded ? (
              canStopTp && (
              <Tooltip title={i18next.t("light.detail.stopTracking")} placement="bottom" disableInteractive>
                <button
                  onClick={() => setStopTpDialogOpen(true)}
                  style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: RADIUS.sm, padding: "0.35rem 0.75rem", color: "rgba(255,255,255,0.85)", cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 600, transition: "background 0.15s", flexShrink: 0 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; }}
                >
                  <StopCircleOutlinedIcon style={{ fontSize: "15px" }} />
                  {i18next.t("light.detail.stopTracking")}
                </button>
              </Tooltip>
              )
            ) : (
              canReactivateTp && (
              <button
                onClick={handleReactivateThirdparty}
                disabled={reactivatingTp}
                style={{ display: "flex", alignItems: "center", gap: "0.35rem", background: BRAND.secondary, border: "none", borderRadius: RADIUS.sm, padding: "0.4rem 0.85rem", color: BRAND.primary, cursor: "pointer", fontFamily: FONT.family, ...FONT.small, fontWeight: 700, transition: "opacity 0.15s", flexShrink: 0 }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.85"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
              >
                {reactivatingTp ? i18next.t("light.detail.reactivating") : i18next.t("light.detail.reactivateTracking")}
              </button>
              )
            )}
          </Box>
          {(tp.thirdpartyAlerts === "OUI" || tp.thirdpartyAlerts === "CLOTURÉE") && (
            <Box style={{ marginTop: "0.3rem" }}>
              {tp.thirdpartyAlerts === "OUI" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "#FEF3C7", color: "#B45309", borderRadius: "20px", padding: "2px 8px", fontSize: "0.65rem", fontWeight: 700, fontFamily: FONT.family }}>
                  {i18next.t("light.detail.collectiveProceedings")}
                </span>
              )}
              {tp.thirdpartyAlerts === "CLOTURÉE" && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: "#FEE2E2", color: "#B91C1C", borderRadius: "20px", padding: "2px 8px", fontSize: "0.65rem", fontWeight: 700, fontFamily: FONT.family }}>
                  {i18next.t("light.detail.collectiveProceedingsClosed")}
                </span>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box className={light.pageInner} style={{ marginTop: "-0.75rem" }}>

        {/* ── Info + Indicators two-column ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: indicators.length > 0 ? { xs: "1fr", md: "1fr 400px" } : "1fr", gap: "1rem", marginBottom: "1.25rem", alignItems: "center" }}>
        <Box className={light.sectionCard} style={{ marginBottom: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <Box style={{ padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {/* Info badges: country flag, category, headquarter */}
              <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", alignItems: "center" }}>
                {tp.addressCountryCode && (
                  <Box style={{ display: "inline-flex", alignItems: "center", gap: "0.3rem", backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}`, borderRadius: RADIUS.sm, padding: "2px 8px", ...FONT.small, fontFamily: FONT.family, color: BRAND.textMain }}>
                    <img
                      src={`https://flagcdn.com/16x12/${tp.addressCountryCode.toLowerCase()}.png`}
                      alt={tp.addressCountryCode}
                      style={{ width: "14px", height: "10px", borderRadius: "1px", objectFit: "cover" }}
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    {tp.addressCountryCode}
                  </Box>
                )}
                {tp.headquarter != null && (
                  <Box style={{ display: "inline-flex", alignItems: "center", backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}`, borderRadius: RADIUS.sm, padding: "2px 8px", ...FONT.small, fontFamily: FONT.family, color: BRAND.textMain }}>
                    {tp.headquarter ? i18next.t("light.detail.headquarters") : i18next.t("light.detail.secondaryEstablishment")}
                  </Box>
                )}
                {tp.category && tp.category !== "NC" && (
                  <Box style={{ display: "inline-flex", alignItems: "center", backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}`, borderRadius: RADIUS.sm, padding: "2px 8px", ...FONT.small, fontFamily: FONT.family, color: BRAND.textMain }}>
                    {{ PME: i18next.t("light.detail.categoryPME"), ETI: i18next.t("light.detail.categoryETI"), GE: i18next.t("light.detail.categoryGE"), AUT: i18next.t("light.detail.categoryAUT") }[tp.category] || tp.category}
                  </Box>
                )}
              </Box>

              {/* Identifiers */}
              {identifiers.length > 0 && (
                <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <TagOutlinedIcon style={{ fontSize: "15px", color: BRAND.primary, flexShrink: 0 }} />
                  <Box style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.15rem" }}>
                    {identifiers.map((id, i) => (
                      <React.Fragment key={id.label}>
                        {i > 0 && <span style={{ margin: "0 0.25rem" }}>·</span>}
                        <Tooltip title={i18next.t("light.detail.clickToCopy")} placement="top" enterDelay={400} disableInteractive>
                          <span
                            style={{ cursor: "pointer", transition: "color 0.15s" }}
                            onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(id.value); }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                          >
                            {id.value} <span style={{ opacity: 0.6 }}>({id.label})</span>
                          </span>
                        </Tooltip>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Company info — collapsible on mobile */}
              {(tp.etabActivityLabel || tp.legalCategoryLabel || tp.companyStartDate || tp.capital != null || tp.workforce || tp.turnover != null || tp.principals?.length > 0) && (
              <Box
                onClick={() => setLegalInfoOpen((o) => !o)}
                style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer", userSelect: "none", paddingTop: "0.25rem" }}
              >
                <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, flex: 1 }}>
                  {i18next.t("light.detail.legalInfo")}
                </Typography>
                <ExpandMoreIcon style={{ fontSize: "17px", color: BRAND.textMuted, transform: legalInfoOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
              </Box>
              )}
              <Collapse in={legalInfoOpen} timeout={200} unmountOnExit>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "0.5rem 1.5rem", padding: "0.5rem 0" }}>
                {tp.etabActivityLabel && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.activity")}</Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{tp.etabActivityLabel}</Typography>
                  </Box>
                )}
                {tp.legalCategoryLabel && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.legalForm")}</Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{tp.legalCategoryLabel}</Typography>
                  </Box>
                )}
                {(tp.companyStartDate || tp.capital != null) && (
                  <Box style={{ display: "flex", gap: "2rem" }}>
                    {tp.companyStartDate && (
                      <Box>
                        <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.creation")}</Typography>
                        <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{formatDt(tp.companyStartDate)}</Typography>
                      </Box>
                    )}
                    {tp.capital != null && (
                      <Box>
                        <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.capital")}</Typography>
                        <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{tp.capital.toLocaleString("fr-FR")} {tp.capitalCurrency ?? "€"}</Typography>
                      </Box>
                    )}
                  </Box>
                )}
                {tp.workforce != null && tp.workforce > 0 && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.workforce")}</Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>{tp.workforce}</Typography>
                  </Box>
                )}
                {tp.turnover != null && (
                  <Box>
                    <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.15rem" }}>{i18next.t("light.detail.turnover")}</Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>
                      {new Intl.NumberFormat("fr-FR", { style: "currency", currency: tp.turnoverCurrency ?? "EUR", notation: "compact", maximumFractionDigits: 1 }).format(tp.turnover)}
                      {tp.lastBalanceSheetDate && (
                        <span style={{ ...FONT.meta, fontWeight: 400, color: BRAND.textMuted, marginLeft: "0.4rem" }}>
                          ({new Date(tp.lastBalanceSheetDate).toLocaleDateString("fr-FR", { year: "numeric", month: "short" })})
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}
                {tp.principals?.length > 0 && (
                  <Box style={{ gridColumn: "1 / -1" }}>
                    <Typography
                      onClick={() => setPrincipalsOpen((o) => !o)}
                      style={{ ...FONT.meta, color: BRAND.primary, fontFamily: FONT.family, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.25rem", fontWeight: 600 }}
                    >
                      {i18next.t("light.detail.directors", { count: tp.principals.length })}
                      <ExpandMoreIcon style={{ fontSize: "14px", transform: principalsOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
                    </Typography>
                    <Collapse in={principalsOpen} timeout={200} unmountOnExit>
                      <Box style={{ display: "flex", flexDirection: "column", gap: "0.35rem", marginTop: "0.4rem" }}>
                        {tp.principals.map((p, i) => (
                          <Box key={i} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0.75rem", borderRadius: RADIUS.sm, backgroundColor: BRAND.background, border: `1px solid ${BRAND.border}` }}>
                            <Box style={{ width: "26px", height: "26px", borderRadius: "50%", backgroundColor: BRAND.primary, color: BRAND.white, display: "flex", alignItems: "center", justifyContent: "center", ...FONT.tiny, fontWeight: 700, flexShrink: 0 }}>
                              {[p.firstname?.[0], p.name?.[0]].filter(Boolean).join("").toUpperCase()}
                            </Box>
                            <Box>
                              <Typography style={{ ...FONT.small, fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family }}>
                                {`${p.firstname ?? ""} ${p.name ?? ""}`.trim()}
                              </Typography>
                              {p.function && (
                                <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>{p.function}</Typography>
                              )}
                            </Box>
                          </Box>
                      ))}
                    </Box>
                  </Collapse>
                  </Box>
                )}
              </Box>
              </Collapse>

              {/* Address + map link */}
                {(tp.addressStreet || tp.addressCity) && (
                  <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <LocationOnOutlinedIcon style={{ fontSize: "15px", color: BRAND.primary, flexShrink: 0 }} />
                    <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family }}>{[tp.addressStreet, tp.additionalAddressDetails, tp.addressPostCode, tp.addressCity, tp.addressCountryCode].filter(Boolean).join(", ")}</span>
                    <span style={{ ...FONT.small, color: BRAND.textMuted }}>·</span>
                    <span
                      onClick={() => setMapOpen(true)}
                      style={{ ...FONT.small, color: BRAND.primary, fontFamily: FONT.family, fontWeight: 600, cursor: "pointer", flexShrink: 0, transition: "opacity 0.15s", whiteSpace: "nowrap", textDecoration: "underline" }}
                      onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.7"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                    >
                      {i18next.t("light.detail.viewOnMap")}
                    </span>
                  </Box>
                )}


              {/* Attributes (text inline) — only if account has TP attribute definitions */}
              {hasTpAttrDefs && canViewTpAttributes && <Box style={{ paddingTop: "0.6rem", borderTop: `1px solid ${BRAND.border}` }}>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.3rem" }}>
                  <span style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.detail.attributes")}</span>
                  {tp.onboarded && (
                    <button
                      onClick={() => setEditAttrsOpen(true)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", borderRadius: RADIUS.sm, transition: "color 0.15s", display: "flex", alignItems: "center" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = BRAND.primary; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = BRAND.textMuted; }}
                    >
                      <EditOutlinedIcon style={{ fontSize: "14px" }} />
                    </button>
                  )}
                </Box>
                {attrs.length === 0 ? (
                  <span style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, fontStyle: "italic" }}>{i18next.t("light.detail.noAttribute")}</span>
                ) : (
                  <Box style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
                    {attrs.map((a) => {
                      const vals = Array.isArray(a.values) ? a.values : [a.values];
                      return (
                        <Tooltip key={a.label} title={vals.length > 1 ? <>{vals.map((v, i) => <div key={i}>{v}</div>)}</> : `${a.label} : ${vals[0]}`} placement="top" enterDelay={200} disableInteractive>
                          <Box className={light.attrTag}>
                            <span className={light.attrTagLabel}>{a.label}</span>
                            <span className={light.attrTagValue}>{vals.join(", ")}</span>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>}

            </Box>
          </Box>

          {/* Right column: Indicators */}
          {canViewTpIndicators && indicators.length > 0 && (
            <Box className={light.sectionCard} style={{ marginBottom: 0 }}>
              <Box className={light.sectionHead}>
                <Typography className={light.sectionTitle}>{i18next.t("light.detail.indicators")}</Typography>
                <Box className={light.sectionCount}>{indicators.length}</Box>
              </Box>
              <Box style={{ display: "flex", flexDirection: "column", gap: "0.4rem", padding: "0.75rem 1rem" }}>
              {indicators.map((ind) => {
                const color = normColor(ind.resultColorCode) || "#94A3B8";
                const val = formatCalculationResult(ind);
                const displayVal = val === true ? i18next.t("yes") : val === false ? i18next.t("no") : val;
                return (
                  <Box key={ind.name} style={{
                    backgroundColor: `${color}10`, borderRadius: RADIUS.sm,
                    border: `1px solid ${color}20`, padding: "0.5rem 0.75rem",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem",
                  }}>
                    <Typography style={{ ...FONT.small, color: BRAND.textMain, fontFamily: FONT.family, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                      {ind.label || ind.name}
                    </Typography>
                    <Typography style={{ ...FONT.small, fontWeight: 700, color, fontFamily: FONT.family, flexShrink: 0, whiteSpace: "nowrap" }}>
                      {displayVal ?? "—"}
                    </Typography>
                  </Box>
                );
              })}
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Contexts ── */}
        {tpContexts.length > 0 && (
          <Box className={light.sectionCard}>
            <Box className={light.sectionHead}>
              <AccountTreeOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
              <Typography className={light.sectionTitle}>{tpContexts[0]?.instanceInfo?.contextModelName || i18next.t("light.contexts.title")}</Typography>
              <Box className={light.sectionCount}>{tpContexts.length}</Box>
            </Box>
            {tpContexts.length === 0 ? (
              <Box className={light.emptySection}>{i18next.t("light.detail.noContext")}</Box>
            ) : (
              <Box style={{ display: "flex", flexDirection: "column" }}>
                {tpContexts.map((ctx) => {
                  const inst = ctx.instanceInfo ?? {};
                  const ref = inst.reference || `#${ctx.ctxInstanceId}`;
                  return (
                    <Box
                      key={ctx.id ?? ctx.ctxInstanceId}
                      component="a"
                      href={getUrlWithAccountId(state.account.id, `light/contexts/${ctx.ctxInstanceId}`)}
                      onClick={(e) => { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); history.push({ pathname: getUrlWithAccountId(state.account.id, `light/contexts/${ctx.ctxInstanceId}`), state: { fromThirdpartyId: thirdpartyId } }); }}
                      style={{
                        display: "flex", alignItems: "center", gap: "0.75rem",
                        padding: "0.85rem 1.25rem", borderBottom: `1px solid ${BRAND.border}`,
                        cursor: "pointer", transition: "background 0.12s", textDecoration: "none", color: "inherit",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND.background; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                    >
                      <Box style={{ width: "36px", height: "36px", borderRadius: RADIUS.md, backgroundColor: BRAND.background, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <AccountTreeOutlinedIcon style={{ fontSize: "18px", color: BRAND.primary }} />
                      </Box>
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Typography style={{ fontSize: "0.875rem", fontWeight: 600, color: BRAND.textMain, fontFamily: FONT.family, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {ref}
                        </Typography>
                      </Box>
                      <ChevronRightIcon style={{ fontSize: "16px", color: BRAND.textMuted, flexShrink: 0 }} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ── Evaluations (accordion) ── */}
        <Box className={light.sectionCard}>
          <Box className={light.sectionHead}>
            <FolderOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.evaluations")}</Typography>
            <Box className={light.sectionCount}>{evaluations.length}</Box>
            {tp.onboarded && (
              <Box sx={{ marginLeft: { xs: 0, sm: "auto" } }}>
                <button
                  className={light.btnPrimary}
                  style={{ padding: "0.35rem 0.75rem", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "4px" }}
                  onClick={() => history.push({
                    pathname: getUrlWithAccountId(state.account.id, ROUTES.LIGHT_LAUNCH),
                    state: { thirdparty: { thirdpartyId, name: tp.name, identifier: tp.siret || tp.siren || tp.duns || tp.tva || "", city: tp.addressCity, postCode: tp.addressPostCode, onboarded: true } },
                  })}
                >
                  + {i18next.t("light.detail.launchEval")}
                </button>
              </Box>
            )}
          </Box>
          {evaluations.length === 0 ? (
            <Box className={light.emptySection}>{i18next.t("light.detail.noEvalForTp")}</Box>
          ) : (
            <Box className={s.dossierList}>
              {evaluations.map((d) => (
                <DossierAccordion
                  key={d.dossierId}
                  dossier={d}
                  accountId={state.account.id}
                  dispatch={dispatch}
                  onStopped={() => {
                    // Refresh active list + bump archived count
                    if (tp?.name) {
                      getAccountDossiersSearch(
                        state.account.id, true, null,
                        { page: 0, rowPerPage: 50 },
                        { dossiers: [{ name: tp.name }] },
                        [{ field: "creationDate", sort: "desc" }],
                        dispatch,
                      ).then((r) => setEvaluations(r?.content ?? [])).catch(() => {});
                    }
                    setArchivedEvalsCount((c) => c + 1);
                    setArchivedEvalsLoaded(false);
                  }}
                  onChanged={(dossierId, newStatus) => {
                    if (dossierId && newStatus) {
                      setEvaluations((prev) => prev.map((ev) => ev.dossierId === dossierId ? { ...ev, statusCode: newStatus } : ev));
                    }
                  }}
                  readOnly={!tp.onboarded}
                  hasDossierAttrDefs={hasDossierAttrDefs}
                  reopenDoc={reopenDoc?.dossierId === d.dossierId ? reopenDoc : null}
                  onDocReopened={() => setReopenDoc(null)}
                  contextName={dossierContextMap[d.dossierId] || ""}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* ── Archived evaluations (only if any) ── */}
        {archivedEvalsCount > 0 && <Box className={light.sectionCard}>
          <Box
            className={light.sectionHead}
            onClick={handleToggleArchivedEvals}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <FolderOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.archivedEvaluations")}</Typography>
            {archivedEvalsLoaded && <Box className={light.sectionCount}>{archivedEvals.length}</Box>}
            <Box style={{ marginLeft: "auto" }}>
              <ExpandMoreIcon style={{
                fontSize: "18px", color: BRAND.textMuted,
                transform: archivedEvalsOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }} />
            </Box>
          </Box>
          <Collapse in={archivedEvalsOpen} timeout={250} unmountOnExit>
            {archivedEvalsLoading ? (
              <Box style={{ padding: "1.5rem" }}>
                <LightLoader size="sm" label={i18next.t("light.common.loading")} />
              </Box>
            ) : archivedEvals.length === 0 ? (
              <Box className={light.emptySection}>{i18next.t("light.detail.noArchivedEval")}</Box>
            ) : (
              <Box className={s.dossierList}>
                {archivedEvals.map((d) => (
                  <DossierAccordion
                    key={d.dossierId}
                    dossier={d}
                    accountId={state.account.id}
                    dispatch={dispatch}
                    onStopped={() => {}}
                    onChanged={() => {}}
                    readOnly
                    onReactivated={() => {
                      setArchivedEvalsCount((c) => Math.max(0, c - 1));
                      setArchivedEvals((prev) => prev.filter((e) => e.dossierId !== d.dossierId));
                      // Refresh active list
                      if (tp?.name) {
                        getAccountDossiersSearch(
                          state.account.id, true, null,
                          { page: 0, rowPerPage: 50 },
                          { dossiers: [{ name: tp.name }] },
                          [{ field: "creationDate", sort: "desc" }],
                          dispatch,
                        ).then((r) => setEvaluations(r?.content ?? [])).catch(() => {});
                      }
                    }}
                    hasDossierAttrDefs={hasDossierAttrDefs}
                    contextName={dossierContextMap[d.dossierId] || ""}
                  />
                ))}
              </Box>
            )}
          </Collapse>
        </Box>}

        {/* ── Contacts ── */}
        <Box className={light.sectionCard}>
          <Box
            className={light.sectionHead}
            onClick={() => setContactsOpen((o) => !o)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <PeopleOutlinedIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.contacts")}</Typography>
            {contacts.length > 0 && <Box className={light.sectionCount}>{contacts.length}</Box>}
            {!loading && contacts.filter((c) => c.status === "INVITE" || c.status === "INSCRIT").length === 0 && (
              <Tooltip title={`${i18next.t("light.detail.noContact")} — ${i18next.t("light.detail.noContactWarning")}`} placement="top" disableInteractive>
                <Box style={{ display: "flex", alignItems: "center", gap: "0.3rem", backgroundColor: "#FFF7ED", color: "#B45309", borderRadius: "20px", padding: "2px 8px", fontSize: "0.68rem", fontWeight: 700, fontFamily: "Inter", cursor: "default" }}>
                  <WarningAmberIcon style={{ fontSize: "12px" }} />
                  {i18next.t("light.detail.noContact")}
                </Box>
              </Tooltip>
            )}
            <Box style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              {tp.onboarded && canCreateContact && (
                <button
                  className={light.btnPrimary}
                  style={{ padding: "0.3rem 0.75rem", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "4px" }}
                  onClick={(e) => { e.stopPropagation(); setInviteOpen(true); }}
                >
                  <PersonAddOutlinedIcon style={{ fontSize: "13px" }} /> {i18next.t("light.detail.invite")}
                </button>
              )}
              <ExpandMoreIcon style={{
                fontSize: "18px", color: BRAND.textMuted,
                transform: contactsOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }} />
            </Box>
          </Box>

          {/* Invite form */}
          {inviteOpen && (
            <Box style={{ padding: "1rem", borderTop: `1px solid ${BRAND.border}`, display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <Box style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
                <input
                  type="email"
                  placeholder={i18next.t("light.detail.emailPlaceholder")}
                  value={inviteEmail}
                  onChange={(e) => { setInviteEmail(e.target.value); if (inviteError) setInviteError(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") handleInviteContact(); if (e.key === "Escape") { setInviteOpen(false); setInviteEmail(""); setInviteError(""); } }}
                  style={{ flex: 1, minWidth: "200px", border: `1.5px solid ${inviteError ? "#DC2626" : BRAND.border}`, borderRadius: "8px", padding: "0.45rem 0.75rem", fontFamily: "Inter, sans-serif", fontSize: "0.85rem", outline: "none" }}
                  autoFocus
                />
                <select
                  value={inviteLang}
                  onChange={(e) => setInviteLang(e.target.value)}
                  style={{ border: `1.5px solid ${BRAND.border}`, borderRadius: "8px", padding: "0.45rem 0.5rem", fontFamily: "Inter, sans-serif", fontSize: "0.85rem", outline: "none", backgroundColor: BRAND.white, cursor: "pointer" }}
                >
                  {getUserLanguages().map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>
                <button className={light.btnPrimary} style={{ padding: "0.45rem 1rem", fontSize: "0.82rem" }} onClick={handleInviteContact} disabled={!inviteEmail.trim() || inviteLoading}>
                  {inviteLoading ? i18next.t("light.detail.sending") : i18next.t("light.detail.sendInvitation")}
                </button>
                <button className={light.btnSecondary} style={{ padding: "0.45rem 1rem", fontSize: "0.82rem" }} onClick={() => { setInviteOpen(false); setInviteEmail(""); setInviteError(""); }}>
                  {i18next.t("cancel")}
                </button>
              </Box>
              {inviteError && (
                <Typography style={{ ...FONT.small, color: "#DC2626", fontFamily: FONT.family }}>{inviteError}</Typography>
              )}
            </Box>
          )}

          {/* Contact table */}
          <Collapse in={contactsOpen} timeout={250} unmountOnExit>
            {contacts.length === 0 ? (
              <Box className={s.emptyContacts}>{i18next.t("light.detail.noContactForTp")}</Box>
            ) : (() => {
              const q = contactSearch.toLowerCase().trim();
              const filtered = q
                ? contacts.filter((c) =>
                    [c.firstName, c.lastName, c.email, c.function].filter(Boolean).join(" ").toLowerCase().includes(q)
                  )
                : contacts;
              return (
              <>
              {contacts.length > 3 && (
                <Box style={{ padding: "0.6rem 1rem", borderBottom: `1px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <SearchOutlinedIcon style={{ fontSize: "15px", color: BRAND.textMuted }} />
                  <input
                    type="text"
                    placeholder={i18next.t("light.detail.searchContact")}
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    style={{ flex: 1, border: "none", outline: "none", fontFamily: FONT.family, ...FONT.body, color: BRAND.textMain, backgroundColor: "transparent" }}
                  />
                  {contactSearch && (
                    <button
                      onClick={() => setContactSearch("")}
                      style={{ border: "none", background: "transparent", cursor: "pointer", color: BRAND.textMuted, padding: "2px", display: "flex" }}
                    >
                      <CloseIcon style={{ fontSize: "14px" }} />
                    </button>
                  )}
                </Box>
              )}
              {filtered.length === 0 ? (
                <Box className={s.emptyContacts}>{i18next.t("light.detail.noContactFound")}</Box>
              ) : (
              <Box className={s.contactTableWrap}>
              <table className={s.contactTable}>
                <thead>
                  <tr>
                    <th className={s.contactTh}>{i18next.t("light.detail.contactName")}</th>
                    <th className={s.contactTh}>{i18next.t("light.detail.contactFunction")}</th>
                    <th className={s.contactTh}>{i18next.t("light.detail.contactEmail")}</th>
                    <th className={s.contactTh}>{i18next.t("light.detail.contactStatus")}</th>
                    <th className={s.contactTh} style={{ width: "100px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const initials = [c.firstName?.[0], c.lastName?.[0]].filter(Boolean).join("").toUpperCase() || "?";
                    const isRegistered = c.status === "INSCRIT";
                    const statusCfg = CONTACT_STATUS[c.status] ?? { label: c.status ?? "—", bg: BRAND.background, color: BRAND.textMuted };
                    return (
                      <tr key={c.pcuId} className={s.contactTr}>
                        <td className={s.contactTd}>
                          <Box style={{ display: "flex", alignItems: "center" }}>
                            <Box className={s.contactAvatar}>{initials}</Box>
                            <Box>
                              <Typography className={s.contactName}>
                                {[c.firstName, c.lastName].filter(Boolean).join(" ") || "—"}
                              </Typography>
                              {c.phone && <Typography className={s.contactEmail}>{c.phone}</Typography>}
                            </Box>
                          </Box>
                        </td>
                        <td className={s.contactTd} style={{ color: BRAND.textMuted, fontSize: "0.8rem" }}>
                          {c.function || "—"}
                        </td>
                        <td className={s.contactTd}>
                          <Typography className={s.contactEmail} style={{ fontSize: "0.8rem", color: BRAND.textMain }}>{c.email}</Typography>
                        </td>
                        <td className={s.contactTd}>
                          <Box style={{ display: "inline-flex", alignItems: "center", borderRadius: "20px", padding: "3px 10px", fontSize: "0.7rem", fontWeight: 700, fontFamily: "Inter", backgroundColor: statusCfg.bg, color: statusCfg.color }}>
                            {statusCfg.label}
                          </Box>
                        </td>
                        {tp.onboarded && (
                          <td className={s.contactTd}>
                            <Box className={s.contactActions}>
                              <button
                                className={s.contactActionBtn}
                                disabled={!isRegistered}
                                title={i18next.t("light.detail.sendEmail")}
                                onClick={() => { window.location = `mailto:${c.email}`; }}
                              >
                                <EmailOutlinedIcon style={{ fontSize: "14px" }} />
                              </button>
                              {canCreateContact && (
                              <button
                                className={s.contactActionBtn}
                                disabled={isRegistered}
                                title={i18next.t("light.detail.reinviteContact")}
                                onClick={() => handleReinviteContact(c)}
                              >
                                <PersonAddOutlinedIcon style={{ fontSize: "14px" }} />
                              </button>
                              )}
                              {canDeleteContact && (
                              <button
                                className={`${s.contactActionBtn} ${s.contactActionDelete}`}
                                disabled={isRegistered}
                                title={i18next.t("delete")}
                                onClick={() => setDeleteContactTarget(c)}
                              >
                                <DeleteOutlineIcon style={{ fontSize: "14px" }} />
                              </button>
                              )}
                            </Box>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </Box>
              )}
              </>
              );
            })()}
          </Collapse>
        </Box>

        {/* ── Notes fournisseur ── */}
        <Box className={light.sectionCard}>
          <Box
            className={light.sectionHead}
            onClick={() => setTpNotesOpen((o) => !o)}
            style={{ cursor: "pointer", userSelect: "none" }}
          >
            <ChatBubbleOutlineIcon style={{ fontSize: "16px", color: BRAND.textMuted }} />
            <Typography className={light.sectionTitle}>{i18next.t("light.detail.tpNotes")}</Typography>
            {tpNotes.length > 0 && <Box className={light.sectionCount}>{tpNotes.length}</Box>}
            <Box style={{ marginLeft: "auto" }}>
              <ExpandMoreIcon style={{
                fontSize: "18px", color: BRAND.textMuted,
                transform: tpNotesOpen ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }} />
            </Box>
          </Box>

          <Collapse in={tpNotesOpen} timeout={250} unmountOnExit>
            {/* Note input */}
            {tp.onboarded && canCreateTpNotes && <Box style={{ padding: "1rem 1.25rem", borderTop: `1px solid ${BRAND.border}`, display: "flex", gap: "0.5rem", alignItems: "flex-start" }}>
              <Box className={s.mentionInputWrap} style={{ flex: 1 }}>
                {tpMention.mentionQuery !== null && tpMention.filteredUsers.length > 0 && (
                  <Box className={s.mentionDropdown} ref={tpMention.dropdownRef}>
                    {tpMention.filteredUsers.map((u, idx) => {
                      const full = `${u.firstname ?? ""} ${u.name ?? ""}`.trim();
                      const initials = [u.firstname?.[0], u.name?.[0]].filter(Boolean).join("").toUpperCase();
                      return (
                        <Box
                          key={u.userId ?? idx}
                          className={`${s.mentionItem} ${idx === tpMention.mentionIndex ? s.mentionItemActive : ""}`}
                          onMouseDown={(e) => { e.preventDefault(); tpMention.insertMention(u); }}
                        >
                          <Box className={s.mentionAvatar}>{initials}</Box>
                          <Typography className={s.mentionName}>{full}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                <textarea
                  ref={tpMention.textareaRef}
                  className={s.noteTextarea}
                  style={{ width: "100%" }}
                  placeholder={i18next.t("light.detail.addNote")}
                  value={tpNoteText}
                  onChange={tpMention.handleTextChange}
                  onKeyDown={(e) => tpMention.handleKeyDown(e, handleAddTpNote)}
                  rows={2}
                />
              </Box>
              <button className={s.noteSendBtn} onClick={handleAddTpNote} disabled={!tpNoteText.trim() || tpSavingNote}>
                <SendIcon style={{ fontSize: "14px" }} />
              </button>
            </Box>}

            {/* Note list */}
            {canViewTpNotes && (tpNotes.length === 0 ? (
              <Box style={{ padding: "1.5rem", textAlign: "center", ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family }}>
                {i18next.t("light.detail.noNoteYet")}
              </Box>
            ) : (
              <Box style={{ display: "flex", flexDirection: "column", padding: "0.5rem 1.25rem 1.25rem", gap: "0.5rem" }}>
                <LightNotesList notes={tpNotes} onDelete={handleDeleteTpNote} canDelete={tp.onboarded && canCreateTpNotes} classes={s} />
              </Box>
            ))}
          </Collapse>
        </Box>

      </Box>
    </Box>

    {/* ── Dialog attributs tiers ── */}
    <Dialog
      open={editAttrsOpen}
      onClose={() => setEditAttrsOpen(false)}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: "16px", fontFamily: "Inter, sans-serif", maxHeight: "85vh" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "Inter", fontSize: "1rem", fontWeight: 700, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5, flexShrink: 0 }}>
        <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <BusinessOutlinedIcon style={{ fontSize: "18px", color: "#54CC54" }} />
          {i18next.t("light.detail.attributes")} — {tp?.name}
        </Box>
        <IconButton size="small" onClick={() => setEditAttrsOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 3, overflowY: "auto" }}>
        <LightEditableAttributes
          accountId={state.account?.id}
          entityId={thirdpartyId}
          bondType="thirdparty"
          onSaved={() => {
            getThirdpartyAttributes(state.account.id, thirdpartyId, null)
              .then((r) => setAttrs((r?.content ?? []).filter((a) => a.values?.length > 0)))
              .catch(() => {});
          }}
        />
      </DialogContent>
    </Dialog>

    {/* ── Delete contact confirmation dialog ── */}
    <Dialog
      open={Boolean(deleteContactTarget)}
      onClose={() => setDeleteContactTarget(null)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family } }}
    >
      <Box style={{ padding: "1.5rem" }}>
        <Typography style={{ ...FONT.h2, color: BRAND.textMain, fontFamily: FONT.family, marginBottom: "0.75rem" }}>
          {i18next.t("light.detail.deleteContact")}
        </Typography>
        <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.6 }}>
          {i18next.t("light.detail.deleteContactConfirm")} <strong>{[deleteContactTarget?.firstName, deleteContactTarget?.lastName].filter(Boolean).join(" ") || deleteContactTarget?.email}</strong> ?
        </Typography>
        <Box style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.25rem" }}>
          <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={() => setDeleteContactTarget(null)} disabled={deletingContact}>
            {i18next.t("cancel")}
          </button>
          <button className={s.btnDanger} onClick={handleConfirmDeleteContact} disabled={deletingContact}>
            {deletingContact ? i18next.t("light.detail.deleting") : i18next.t("delete")}
          </button>
        </Box>
      </Box>
    </Dialog>

    {/* ── Map dialog (OpenStreetMap) ── */}
    {(tp?.addressStreet || tp?.addressCity) && (() => {
      const mapQuery = [tp.addressStreet, tp.additionalAddressDetails, tp.addressPostCode, tp.addressCity, tp.addressCountryCode].filter(Boolean).join(", ");
      const osmSearchUrl = `https://www.openstreetmap.org/search?query=${encodeURIComponent(mapQuery)}#map=16`;
      return (
        <Dialog
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: RADIUS.lg, fontFamily: FONT.family, overflow: "hidden" } }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: FONT.family, ...FONT.h2, color: BRAND.white, backgroundColor: BRAND.primary, pb: 1.5 }}>
            <Box style={{ display: "flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
              <LocationOnOutlinedIcon style={{ fontSize: "18px", flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tp?.name}</span>
            </Box>
            <IconButton size="small" onClick={() => setMapOpen(false)} sx={{ color: "rgba(255,255,255,0.7)", "&:hover": { color: BRAND.white } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <Box style={{ padding: "0.6rem 1rem", borderBottom: `1px solid ${BRAND.border}`, ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family }}>
            <LocationOnOutlinedIcon style={{ fontSize: "13px", color: BRAND.primary, verticalAlign: "middle", marginRight: "0.3rem" }} />
            {mapQuery}
          </Box>
          <MapIframe
            street={tp?.addressStreet}
            postCode={tp?.addressPostCode}
            city={tp?.addressCity}
            countryCode={tp?.addressCountryCode}
          />
          <Box style={{ display: "flex", justifyContent: "flex-end", padding: "0.6rem 1rem", borderTop: `1px solid ${BRAND.border}` }}>
            <button
              onClick={() => window.open(osmSearchUrl, "_blank")}
              style={{ display: "flex", alignItems: "center", gap: "0.3rem", border: `1px solid ${BRAND.border}`, borderRadius: RADIUS.sm, padding: "0.4rem 0.85rem", backgroundColor: BRAND.white, cursor: "pointer", ...FONT.small, fontFamily: FONT.family, color: BRAND.primary, fontWeight: 600, transition: "border-color 0.15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = BRAND.primary; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = BRAND.border; }}
            >
              {i18next.t("light.detail.openOSM")}
            </button>
          </Box>
        </Dialog>
      );
    })()}

    <StopConfirmDialog
      open={stopTpDialogOpen}
      onClose={() => { setStopTpDialogOpen(false); setStopTpReason(""); }}
      onConfirm={handleStopThirdparty}
      loading={stoppingTp}
      title={i18next.t("light.detail.stopTracking")}
      reasonLabel={i18next.t("light.detail.stopTpReasonLabel")}
      reasonPlaceholder={i18next.t("light.detail.stopTpReasonPlaceholder")}
      reason={stopTpReason}
      onReasonChange={setStopTpReason}
      cancelLabel={i18next.t("cancel")}
      confirmLabel={i18next.t("light.detail.stopTracking")}
      confirmingLabel={i18next.t("light.detail.stoppingTp")}
    >
      {i18next.t("light.detail.stopTpConfirm")} <strong>{tp?.name}</strong> ? {i18next.t("light.detail.stopTpReversible")}
    </StopConfirmDialog>
    </>
  );
}
